import { TextInput } from '@mantine/core';
import { Toggle } from '@carbon/react';
import moment from 'moment';
import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';
import { toDateTimeFormat } from 'utils/timeDateUtils';
import { syncRecurrenceWithStartDate } from 'Screens/Dashboard/Events/EventHandler/inputs/EventRecurrence/recurrenceUtils';

import styles from './EventDatePicker.module.scss';

const toInputDate = (value: string) => (value ? moment(value).format('YYYY-MM-DD') : '');

const EventDatePicker = () => {
    const intl = useIntl();
    const { values, updateValues } = useEventContext();
    const { dateFrom, dateTo, isMultiDay } = values;

    const updateDate = useCallback((field: 'dateFrom' | 'dateTo', value: string) => {
        const nextDate = toDateTimeFormat(moment(value, 'YYYY-MM-DD'));

        if (field === 'dateFrom') {
            updateValues({
                ...values,
                dateFrom: nextDate,
                dateTo: isMultiDay ? (values.dateTo || nextDate) : nextDate,
                recurrence: syncRecurrenceWithStartDate(values.recurrence, nextDate),
            });
            return;
        }

        updateValues({
            ...values,
            dateTo: nextDate,
        });
    }, [isMultiDay, updateValues, values]);

    const toggleMultiDay = (checked: boolean) => {
        updateValues({
            ...values,
            isMultiDay: checked,
            dateTo: checked ? values.dateTo : values.dateFrom,
        });
    };

    return (
        <div className={styles.container}>
            <TextInput
                id="date-picker-input-event-date-picker-start"
                label={intl.formatMessage(messages.startDate)}
                type="date"
                value={toInputDate(dateFrom)}
                onChange={(event) => updateDate('dateFrom', event.currentTarget.value)}
                radius="md"
                size="md"
            />
            <div className={styles.toggleContainer}>
                <span>{intl.formatMessage(messages.multiDay)}</span>
                <Toggle
                    id="event-multi-day-toggle"
                    labelA=""
                    labelB=""
                    toggled={isMultiDay}
                    onToggle={toggleMultiDay}
                />
            </div>
            {isMultiDay && (
                <TextInput
                    id="date-picker-input-event-date-picker-finish"
                    label={intl.formatMessage(messages.endDate)}
                    type="date"
                    value={toInputDate(dateTo)}
                    onChange={(event) => updateDate('dateTo', event.currentTarget.value)}
                    radius="md"
                    size="md"
                />
            )}
        </div>
    );
};

const messages = defineMessages({
    startDate: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventPeriod.EventDateTimePicker.EventDatePicker.EventDatePicker.startDate',
        defaultMessage: 'Początek',
    },
    endDate: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventPeriod.EventDateTimePicker.EventDatePicker.EventDatePicker.endDate',
        defaultMessage: 'Koniec',
    },
    multiDay: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventPeriod.EventDateTimePicker.EventDatePicker.EventDatePicker.multiDay',
        defaultMessage: 'Wydarzenie wielodniowe',
    },
});

export default EventDatePicker;
